import { useState, type ReactNode } from "react";
import { Play } from "lucide-react";
import { GoldButton } from "@/components/GoldButton";

/**
 * Renderizador de markdown do corpo do artigo.
 *
 * Simples de propósito: só o que o corpo de um artigo usa. Os títulos saem
 * sempre como H2 e H3, nunca H1, porque o H1 da página é o título do artigo e
 * só pode existir um.
 */

type Bloco =
  | { tipo: "h2" | "h3" | "p"; texto: string }
  | { tipo: "lista" | "numerada"; itens: string[]; inicio?: number }
  | { tipo: "tabela"; cabecalho: string[]; linhas: string[][]; alinhamento: ("left" | "center" | "right")[] };

function celulas(linha: string): string[] {
  return linha.trim().replace(/^\|/, "").replace(/(?<!\\)\|$/, "")
    .split(/(?<!\\)\|/).map((celula) => celula.trim().replace(/\\\|/g, "|"));
}

function separarBlocos(markdown: string): Bloco[] {
  const linhas = markdown.replace(/<!--[\s\S]*?(?:-->|$)/g, "").replace(/\r\n/g, "\n").split("\n");
  const blocos: Bloco[] = [];
  let paragrafo: string[] = [];
  let itens: string[] = [];
  let tipoLista: "lista" | "numerada" = "lista";
  let inicioLista = 1;

  function fecharParagrafo() {
    if (paragrafo.length) {
      blocos.push({ tipo: "p", texto: paragrafo.join(" ") });
      paragrafo = [];
    }
  }
  function fecharLista() {
    if (itens.length) {
      blocos.push({ tipo: tipoLista, itens, inicio: inicioLista });
      itens = [];
    }
  }

  for (let indice = 0; indice < linhas.length; indice += 1) {
    const bruta = linhas[indice] ?? "";
    const linha = bruta.trim();

    if (linha === "") {
      fecharParagrafo();
      fecharLista();
      continue;
    }

    const separadora = celulas(linhas[indice + 1] ?? "");
    const cabecalho = celulas(linha);
    if (linha.includes("|") && cabecalho.length === separadora.length && separadora.every((celula) => /^:?-{3,}:?$/.test(celula))) {
      fecharParagrafo();
      fecharLista();
      const dados: string[][] = [];
      indice += 1;
      while (linhas[indice + 1]?.trim() && linhas[indice + 1]?.includes("|")) {
        indice += 1;
        dados.push(celulas(linhas[indice] ?? ""));
      }
      blocos.push({ tipo: "tabela", cabecalho, linhas: dados, alinhamento: separadora.map((celula) => celula.endsWith(":") ? (celula.startsWith(":") ? "center" : "right") : "left") });
      continue;
    }

    const titulo = /^(#{1,6})\s+(.*)$/.exec(linha);
    if (titulo) {
      fecharParagrafo();
      fecharLista();
      // Um nível de título vira H2; do terceiro em diante, H3.
      const nivel = titulo[1]?.length ?? 2;
      blocos.push({ tipo: nivel >= 3 ? "h3" : "h2", texto: titulo[2] ?? "" });
      continue;
    }

    const item = /^[-*+]\s+(.*)$/.exec(linha);
    const numerado = /^(\d+)\.\s+(.*)$/.exec(linha);
    if (item || numerado) {
      fecharParagrafo();
      const tipo = numerado ? "numerada" : "lista";
      if (tipoLista !== tipo) fecharLista();
      if (!itens.length) inicioLista = numerado ? Number(numerado[1]) : 1;
      tipoLista = tipo;
      itens.push(numerado ? (numerado[2] ?? "") : (item?.[1] ?? ""));
      continue;
    }

    fecharLista();
    paragrafo.push(linha);
  }

  fecharParagrafo();
  fecharLista();
  return blocos;
}

/** Negrito, itálico e links dentro de um trecho de texto. */
/** Links internos /blog/<slug>/ só viram <a> quando o post está público agora. */
export type LinksBlog = { postsPublicos: string[]; categorias: string[] };

function linkBlogIndisponivel(href: string, links?: LinksBlog): boolean {
  if (!links) return false;
  const m = /^\/blog\/([a-z0-9-]+)\/?(?:[?#].*)?$/.exec(href);
  if (!m?.[1] || m[1] === "pagina") return false;
  if (links.categorias.includes(m[1])) return false;
  return !links.postsPublicos.includes(m[1]);
}

function inline(texto: string, chave: string, links?: LinksBlog): ReactNode[] {
  const partes: ReactNode[] = [];
  const padrao = /\*\*(.+?)\*\*|\*(.+?)\*|\[(.+?)\]\((.+?)\)/g;
  let ultimo = 0;
  let achado: RegExpExecArray | null;
  let indice = 0;

  while ((achado = padrao.exec(texto)) !== null) {
    if (achado.index > ultimo) partes.push(texto.slice(ultimo, achado.index));
    indice += 1;
    const id = `${chave}-${indice}`;
    if (achado[1] !== undefined) {
      partes.push(<strong key={id}>{achado[1]}</strong>);
    } else if (achado[2] !== undefined) {
      partes.push(<em key={id}>{achado[2]}</em>);
    } else if (achado[3] !== undefined && achado[4] !== undefined) {
      const href = achado[4];
      if (linkBlogIndisponivel(href, links)) {
        partes.push(achado[3]);
        ultimo = padrao.lastIndex;
        continue;
      }
      const externo = /^https?:\/\//.test(href);
      partes.push(
        <a
          key={id}
          href={href}
          className="text-gold-dark underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark"
          {...(externo ? { rel: "noopener", target: "_blank" } : {})}
        >
          {achado[3]}
        </a>,
      );
    }
    ultimo = padrao.lastIndex;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
}

function videoYoutube(texto: string) {
  const link = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/.exec(texto);
  if (!link?.[1] || !link[2]) return null;
  try {
    const url = new URL(link[2]);
    const host = url.hostname.toLowerCase();
    const id = host === "youtu.be" ? url.pathname.slice(1) :
      ["youtube.com", "www.youtube.com", "m.youtube.com"].includes(host) && url.pathname === "/watch" ? url.searchParams.get("v") : null;
    if (!id || !/^[\w-]{11}$/.test(id)) return null;
    const tempo = url.searchParams.get("t") ?? url.searchParams.get("start") ?? "0";
    const partes = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(tempo);
    const inicio = /^\d+$/.test(tempo) ? Number(tempo) : partes ? Number(partes[1] ?? 0) * 3600 + Number(partes[2] ?? 0) * 60 + Number(partes[3] ?? 0) : 0;
    return { id, inicio, titulo: link[1], href: url.href };
  } catch {
    return null;
  }
}

function VideoYoutube({ video }: { video: NonNullable<ReturnType<typeof videoYoutube>> }) {
  const [ativo, setAtivo] = useState(false);
  return (
    <figure className="artigo-markdown-video">
      <div className="artigo-markdown-video-quadro">
        {ativo ? (
          <iframe src={`https://www.youtube-nocookie.com/embed/${video.id}?${video.inicio ? `start=${video.inicio}&` : ""}autoplay=1`} title={video.titulo} width={560} height={315} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
        ) : (
          <>
            <img src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`} alt={video.titulo} width={480} height={360} loading="lazy" />
            <GoldButton className="artigo-markdown-video-play" aria-label={`Reproduzir vídeo: ${video.titulo}`} onClick={() => setAtivo(true)}><Play aria-hidden="true" /></GoldButton>
          </>
        )}
      </div>
      <figcaption className="t-article-body text-text-reading">{video.titulo}</figcaption>
      <noscript><a href={video.href} className="t-article-body text-gold-dark underline" rel="noopener" target="_blank">{video.titulo}</a></noscript>
    </figure>
  );
}

export function Markdown({ texto, links }: { texto: string; links?: LinksBlog }) {
  const blocos = separarBlocos(texto);

  return (
    <div className="flex flex-col gap-28">
      {blocos.map((bloco, posicao) => {
        const chave = `bloco-${posicao}`;
        if (bloco.tipo === "h2") {
          return (
            <h2 key={chave} className="t-article-h2 text-text-body">
              {inline(bloco.texto, chave, links)}
            </h2>
          );
        }
        if (bloco.tipo === "h3") {
          return (
            <h3 key={chave} className="t-heading-sm text-text-body">
              {inline(bloco.texto, chave, links)}
            </h3>
          );
        }
        if (bloco.tipo === "tabela") {
          return (
            <div key={chave} className="artigo-markdown-tabela" role="region" aria-label="Tabela do artigo" tabIndex={0}>
              <table className="t-article-body text-text-reading">
                <thead><tr>{bloco.cabecalho.map((celula, i) => <th scope="col" className={`artigo-celula-${bloco.alinhamento[i]}`} key={i}>{inline(celula, `${chave}-h-${i}`, links)}</th>)}</tr></thead>
                <tbody>{bloco.linhas.map((linha, j) => <tr key={j}>{bloco.cabecalho.map((_, i) => <td className={`artigo-celula-${bloco.alinhamento[i]}`} key={i}>{inline(linha[i] ?? "", `${chave}-${j}-${i}`, links)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        if (bloco.tipo === "lista" || bloco.tipo === "numerada") {
          const Lista = bloco.tipo === "numerada" ? "ol" : "ul";
          return (
            <Lista key={chave} {...(bloco.tipo === "numerada" ? { start: bloco.inicio } : {})} className={`t-article-body ${bloco.tipo === "numerada" ? "list-decimal" : "list-disc"} pl-16 text-text-reading`}>
              {bloco.itens.map((item, i) => (
                <li key={`${chave}-${i}`}>{inline(item, `${chave}-${i}`, links)}</li>
              ))}
            </Lista>
          );
        }
        if (bloco.tipo !== "p") return null;
        const video = videoYoutube(bloco.texto);
        if (video) return <VideoYoutube key={chave} video={video} />;
        return (
          <p key={chave} className="t-article-body text-text-reading">
            {inline(bloco.texto, chave, links)}
          </p>
        );
      })}
    </div>
  );
}

/** Trecho em linha (negrito, itálico, links), para blocos fora do corpo. */
export function MarkdownInline({ texto }: { texto: string }) {
  return <>{inline(texto, "inline")}</>;
}

export default Markdown;
